/**
 * Panel ichi: widgetlar to'ri, tahrirlash (sudrash, olib tashlash), bitta widget sozlamasi va "Widget qo'shish"
 * galereyasi. Hamma sozlama shu yerda (boshqa joyda takrorlanmaydi). Popover va qadalgan ustun bir xil panelni chizadi.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ChevronLeft, LayoutGrid, Pencil, Pin, PinOff, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { toast } from "@/components/ui/toast";
import { currentLayout, saveLayout } from "./api";
import { canAdd, canToggleGlance, configureItem, inGlance, insertItem, moveItem, newWidgetId, normalize, removeItem, resizeItem, toggleGlance } from "./layout";
import { widgetMenu } from "./menu";
import { WIDGET_LIST, WIDGETS } from "./registry";
import { MAX_GLANCE, MAX_ITEMS, type Layout, type WidgetDef, type WidgetItem, type WidgetSize } from "./types";
import { SortableWidget, WidgetBody, WidgetCard } from "./WidgetFrame";

type Content = Pick<Layout, "items" | "glance">;
type View = { kind: "grid" } | { kind: "settings"; id: string } | { kind: "gallery" };

export function WidgetPanel({ me, layout, pinned, canPin, onPin }: { me: Me; layout: Layout; pinned: boolean; canPin: boolean; onPin: () => void }) {
  const { t } = useTranslation("widgets");
  const [view, setView] = useState<View>({ kind: "grid" });
  const [editing, setEditing] = useState(false);
  // Sudrash tugagach keladigan "click" sozlamani ochib yubormasin
  const draggedAt = useRef(0);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  /** Har amal eng yangi tarkib ustida (menyu va "Qaytarish" kechikib bajariladi) */
  function update(fn: (c: Content) => Content) {
    const cur = currentLayout();
    if (cur) saveLayout(fn(normalize(cur, WIDGETS, me)));
  }

  function remove(item: WidgetItem) {
    const index = layout.items.findIndex((i) => i.id === item.id);
    const wasTop = inGlance(layout, item.id);
    update((c) => removeItem(c, item.id));
    setView({ kind: "grid" });
    toast.undo(t("removed", { name: t(WIDGETS[item.type].title) }), { label: t("undo"), onClick: () => update((c) => insertItem(c, item, index, wasTop)) });
  }

  function add(def: WidgetDef, size: WidgetSize) {
    const item: WidgetItem = { id: newWidgetId(def.type, layout.items.map((i) => i.id)), type: def.type, size, config: def.defaultConfig(me) };
    update((c) => {
      const next = { ...c, items: [...c.items, item] };
      // Birinchi widget yuqori panelda ham ko'rinsin (joy bo'lsa)
      return c.items.length === 0 && c.glance.length < MAX_GLANCE ? { ...next, glance: [...c.glance, item.id] } : next;
    });
    setView({ kind: "grid" });
  }

  function onDragEnd(e: DragEndEvent) {
    draggedAt.current = Date.now();
    const over = e.over?.id;
    if (over !== undefined && over !== e.active.id) update((c) => moveItem(c, String(e.active.id), String(over)));
  }

  const openSettings = (id: string) => {
    if (Date.now() - draggedAt.current > 250) setView({ kind: "settings", id });
  };

  if (view.kind === "settings") {
    const item = layout.items.find((i) => i.id === view.id);
    if (item) {
      return (
        <div key={`settings-${item.id}`} className="widget-view">
          <SettingsView item={item} def={WIDGETS[item.type]} layout={layout} me={me} onBack={() => setView({ kind: "grid" })} onRemove={() => remove(item)} update={update} />
        </div>
      );
    }
  }
  if (view.kind === "gallery") {
    return (
      <div key="gallery" className="widget-view">
        <Gallery me={me} onBack={() => setView({ kind: "grid" })} onAdd={add} />
      </div>
    );
  }

  const ids = layout.items.map((i) => i.id);
  return (
    <div key="grid" className="widget-view">
      <div className="mb-3 flex h-7 items-center justify-between pl-1">
        <span className="text-[12px] font-semibold text-muted">{t("title")}</span>
        {canPin && (
          <button
            type="button"
            onClick={onPin}
            aria-pressed={pinned}
            title={pinned ? t("unpin") : t("pin")}
            aria-label={pinned ? t("unpin") : t("pin")}
            className="rounded-[10px] p-1.5 text-muted transition-colors hover:bg-fill hover:text-ink"
          >
            {pinned ? <PinOff size={15} strokeWidth={1.75} /> : <Pin size={15} strokeWidth={1.75} />}
          </button>
        )}
      </div>

      {layout.items.length === 0 ? (
        <div className="rounded-[22px] border border-dashed border-border px-5 py-8 text-center">
          <LayoutGrid size={22} strokeWidth={1.5} className="mx-auto text-muted" />
          <div className="mt-2 text-[13px] font-semibold">{t("empty")}</div>
          <p className="mt-1 text-[12px] text-muted">{t("emptyHint")}</p>
          <Button size="sm" variant="primary" className="mt-4" onClick={() => setView({ kind: "gallery" })}>
            <Plus size={14} /> {t("add")}
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className={cn("grid grid-cols-2 gap-3 [grid-auto-flow:dense]", editing && "widget-editing")}>
              {layout.items.map((item) => {
                const def = WIDGETS[item.type];
                return (
                  <SortableWidget
                    key={item.id}
                    item={item}
                    def={def}
                    me={me}
                    editing={editing}
                    onOpen={() => openSettings(item.id)}
                    onRemove={() => remove(item)}
                    menu={() =>
                      widgetMenu(
                        item,
                        def,
                        layout,
                        {
                          settings: () => setView({ kind: "settings", id: item.id }),
                          resize: (size) => update((c) => resizeItem(c, item.id, size)),
                          toggleGlance: () => update((c) => toggleGlance(c, item.id)),
                          remove: () => remove(item),
                        },
                        t,
                      )
                    }
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {layout.items.length > 0 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {editing ? (
            <>
              <Button size="sm" onClick={() => setView({ kind: "gallery" })} disabled={!canAdd(layout)} title={canAdd(layout) ? undefined : t("full", { n: MAX_ITEMS })}>
                <Plus size={14} /> {t("add")}
              </Button>
              <Button size="sm" variant="primary" onClick={() => setEditing(false)}>
                {t("done")}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              <Pencil size={13} /> {t("edit")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function ViewHeader({ title, onBack }: { title: string; onBack: () => void }) {
  const { t } = useTranslation("widgets");
  return (
    <div className="mb-3 flex h-7 items-center gap-1">
      <button type="button" onClick={onBack} className="rounded-[10px] p-1 text-muted transition-colors hover:bg-fill hover:text-ink" aria-label={t("back")} title={t("back")}>
        <ChevronLeft size={18} strokeWidth={1.75} />
      </button>
      <span className="text-[13px] font-semibold">{title}</span>
    </div>
  );
}

function SizePicker({ sizes, value, onChange }: { sizes: WidgetSize[]; value: WidgetSize; onChange: (size: WidgetSize) => void }) {
  const { t } = useTranslation("widgets");
  return (
    <div className="grid rounded-2xl bg-fill p-0.5" style={{ gridTemplateColumns: `repeat(${sizes.length}, 1fr)` }} role="radiogroup" aria-label={t("sizeLabel")}>
      {sizes.map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={s === value}
          onClick={() => onChange(s)}
          className={cn("h-7 rounded-[10px] text-[12px] font-medium transition-all", s === value ? "bg-surface text-ink shadow-sm" : "text-ink-soft hover:text-ink")}
        >
          {t(`size.${s}`)}
        </button>
      ))}
    </div>
  );
}

function SettingsView({
  item,
  def,
  layout,
  me,
  onBack,
  onRemove,
  update,
}: {
  item: WidgetItem;
  def: WidgetDef;
  layout: Layout;
  me: Me;
  onBack: () => void;
  onRemove: () => void;
  update: (fn: (c: Content) => Content) => void;
}) {
  const { t } = useTranslation("widgets");
  const Settings = def.Settings;
  const shown = inGlance(layout, item.id);
  return (
    <div>
      <ViewHeader title={t(def.title)} onBack={onBack} />
      <div className="space-y-4">
        {def.sizes.length > 1 && <SizePicker sizes={def.sizes} value={item.size} onChange={(size) => update((c) => resizeItem(c, item.id, size))} />}
        {def.Glance && (
          <Toggle
            label={t("showTop")}
            hint={!canToggleGlance(layout, item.id) ? t("topFull", { n: MAX_GLANCE }) : undefined}
            checked={shown}
            disabled={!canToggleGlance(layout, item.id)}
            onChange={() => update((c) => toggleGlance(c, item.id))}
          />
        )}
        {Settings && <Settings item={item} me={me} onChange={(config) => update((c) => configureItem(c, item.id, config))} />}
        <Button size="sm" variant="danger" className="w-full" onClick={onRemove}>
          <Trash2 size={14} /> {t("removeWidget")}
        </Button>
      </div>
    </div>
  );
}

/** "Widget qo'shish": har tur jonli oldindan ko'rinish bilan, o'lchamini tanlab */
function Gallery({ me, onBack, onAdd }: { me: Me; onBack: () => void; onAdd: (def: WidgetDef, size: WidgetSize) => void }) {
  const { t } = useTranslation("widgets");
  return (
    <div>
      <ViewHeader title={t("add")} onBack={onBack} />
      <div className="space-y-5">
        {WIDGET_LIST.filter((d) => d.available(me)).map((def) => (
          <GalleryItem key={def.type} def={def} me={me} onAdd={onAdd} />
        ))}
      </div>
    </div>
  );
}

function GalleryItem({ def, me, onAdd }: { def: WidgetDef; me: Me; onAdd: (def: WidgetDef, size: WidgetSize) => void }) {
  const { t } = useTranslation("widgets");
  const [size, setSize] = useState<WidgetSize>(def.defaultSize);
  const Icon = def.icon;
  const preview: WidgetItem = { id: "preview", type: def.type, size, config: def.defaultConfig(me) };
  return (
    <section className="space-y-2.5">
      <div className="flex items-start gap-2 px-1">
        <Icon size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
        <div>
          <div className="text-[13px] font-semibold">{t(def.title)}</div>
          <p className="text-[12px] text-muted">{t(def.description)}</p>
        </div>
      </div>
      {def.sizes.length > 1 && <SizePicker sizes={def.sizes} value={size} onChange={setSize} />}
      <div className="grid grid-cols-2 gap-3" aria-hidden>
        <WidgetCard size={size} className={cn("pointer-events-none", size === "s" && "col-start-1")}>
          <WidgetBody def={def} item={preview} me={me} />
        </WidgetCard>
      </div>
      <Button size="sm" variant="primary" className="w-full" onClick={() => onAdd(def, size)}>
        <Plus size={14} /> {t("addThis")}
      </Button>
    </section>
  );
}
